<template>
    <div id="users">
        <a-alert
            v-if="activeUserCount >= features.maxAgentCount"
            :message="$t('message.planInfoHeader')"
            type="info"
            :style="{ backgroundColor: getAlertColor('alert'), color: 'black' }"
            class="mb-2"
        >
            <div slot="description" class="d-flex flex-column">
                <div class="d-flex align-items-center">
                    <a-icon type="info-circle" />
                    <span class="p-1 text-info ml-1">
                        {{
                            $t("message.planInformation", {
                                selectedPlan: getCurrentPlanName(),
                                detailMaxAgentCount: features.maxAgentCount,
                                freeAgent: features.freeAgentCount,
                            })
                        }}
                    </span>
                </div>

                <span class="p-1 text-info mt-1">
                    {{ $t("message.creditAvailable") }}
                    <b>{{ features.maxAgentCount - activeUserCount }}</b> .
                    {{ $t("message.upDownGradePlan") }}
                    <button
                        id="btn-change-plan"
                        style="border-radius: 5px; border: none; background-color: lightskyblue; font-size: larger; padding: 5px; margin-left: 3px;"
                        @click="$router.push('/billing/plans/supsis')"
                    >
                        <b>{{ $t("message.click") }}</b>
                    </button>
                </span>
            </div>
        </a-alert>

        <!-- Gruplama yapılmadığında gösterilecek tablo -->
        <template v-if="groupingType === 'none'">
            <supsis-table
                :columns="cols()"
                :rows="getFilteredRows()"
                :bottom-pagination="true"
                :pagination-cursor="paginationCursor"
                :options="tableOptions"
                @cursor-changed="(cursor) => (paginationCursor = cursor)"
                class="users-supsis-table"
            >
                <template #header>
                    <div
                        class="d-flex justify-content-between align-items-center"
                        id="header-content"
                    >
                        <div class="d-flex gap-2">
                            <a-input-search
                                :value="searchText"
                                @input="searchTextHandle($event)"
                                :placeholder="$t('message.search')"
                                class="search-input"
                                @search="onSearch"
                            />
                            <div class="group-button-container" ref="groupContainer">
                                <CustomPicker
                                    v-model="groupingType"
                                    :items="groupingOptions"
                                    idKey="value"
                                    labelKey="label"
                                    :title="$t('message.groupBy')"
                                    icon="filter_list"
                                    :multiple="false"
                                />
                            </div>
                        </div>
                        <SupsisButton
                            ghost
                            @click="$feature.beforeCreateAgent(openCreateModal)"
                            v-if="checkPermission(PERMISSION_TYPES['user.create'].tr.name)"
                        >
                            <md-icon>add</md-icon> {{ $t("message.createUser") }}
                        </SupsisButton>
                    </div>
                </template>
            </supsis-table>
        </template>

        <!-- Gruplama yapıldığında gösterilecek tablolar -->
        <template v-else>
            <div id="header-content">
                <div class="d-flex justify-content-between align-items-center">
                    <div class="d-flex gap-2">
                        <a-input-search
                            :value="searchText"
                            @input="searchTextHandle($event)"
                            :placeholder="$t('message.search')"
                            class="search-input"
                            @search="onSearch"
                        />
                        <div class="group-button-container" ref="groupContainer">
                            <CustomPicker
                                v-model="groupingType"
                                :items="groupingOptions"
                                idKey="value"
                                labelKey="label"
                                :title="$t('message.groupBy')"
                                icon="filter_list"
                                :multiple="false"
                            />
                        </div>
                    </div>
                    <SupsisButton
                        ghost
                        @click="$feature.beforeCreateAgent(openCreateModal)"
                        v-if="checkPermission(PERMISSION_TYPES['user.create'].tr.name)"
                    >
                        <md-icon>add</md-icon> {{ $t("message.createUser") }}
                    </SupsisButton>
                </div>
            </div>
            <div v-for="(users, groupName) in getGroupedUsers()" :key="groupName" class="group-section mb-4">
                <div
                    class="group-header"
                    @click="togglePanel(groupName)"
                    :style="{ backgroundColor: getGroupColor(groupName), color: getTextColor(groupName) }"
                >
                    <div class="d-flex align-items-center gap-2">
                        <md-icon class="toggle-icon">{{
                            isPanelOpen(groupName) ? "expand_more" : "chevron_right"
                        }}</md-icon>
                        <h3>{{ getGroupDisplayName(groupName) }}</h3>
                    </div>
                    <span class="count">{{ users.length }}</span>
                </div>
                <div v-show="isPanelOpen(groupName)" class="group-content">
                    <supsis-table
                        :columns="cols()"
                        :rows="users"
                        :bottom-pagination="false"
                        :pagination-cursor="paginationCursor"
                        :options="tableOptions"
                        @cursor-changed="(cursor) => (paginationCursor = cursor)"
                    />
                </div>
            </div>
        </template>

        <Modal2
            v-if="isModalShow"
            id="user-form"
            :title="editMode ? $t('message.updateUserForm') : $t('message.newUserForm')"
            :show-footer="false"
            @cancel="hideModal"
        >
            <a-alert
                v-if="$validator.errors.items.length && submitClicked"
                :message="$t('message.error')"
                class="mb-2"
                showIcon
                type="error"
            >
                <div slot="description">
                    <div v-for="(err, index) of $validator.errors.items" :key="index">
                        <span>
                            {{ err.msg }}
                        </span>
                    </div>
                </div>
            </a-alert>
            <b-form class="d-flex flex-column justify-content-center" @submit.prevent="handleSave">
                <b-row>
                    <b-col md="12">
                        <b-row>
                            <b-col md="6">
                                <md-field
                                    :class="{
                                        'md-invalid': errors.first($t('message.username')) != null,
                                    }"
                                >
                                    <label>{{ $t("message.username") }}</label>
                                    <md-input
                                        v-model="user.name"
                                        v-validate="'required'"
                                        :name="$t('message.username')"
                                        type="text"
                                        :placeholder="$t('message.username')"
                                        autocomplete="off"
                                    ></md-input>
                                    <span class="md-error">{{ errors.first($t("message.username")) }}</span>
                                </md-field>
                            </b-col>
                            <b-col md="6">
                                <md-field
                                    :class="{
                                        'md-invalid': errors.first($t('message.email')) != null,
                                    }"
                                >
                                    <label>{{ $t("message.email") }}</label>
                                    <md-input
                                        v-model="user.email"
                                        v-validate="{
                                            required: true,
                                            regex: /^[\w-\.+]+@([\w-]+\.)+[\w-]{2,}(\.\w{2,4})?$/,
                                        }"
                                        :name="$t('message.email')"
                                        autocomplete="off"
                                        trim
                                        type="email"
                                    ></md-input>
                                    <span class="md-error">{{ errors.first($t("message.email")) }}</span>
                                </md-field>
                            </b-col>
                            <b-col md="12">
                                <md-field>
                                    <label>{{ $t("message.phone") }}</label>
                                    <md-input
                                        v-model="user.phone"
                                        v-validate="{
                                            required: false,
                                            regex: /^[0-9]+$/,
                                        }"
                                        :name="$t('message.phone')"
                                        type="tel"
                                        :placeholder="$t('message.phone')"
                                        autocomplete="off"
                                        trim
                                    ></md-input>
                                    <span class="md-error">{{ errors.first($t("message.phone")) }}</span>
                                </md-field>
                            </b-col>
                            <b-col md="12" v-if="shouldShowContent && editMode">
                                <md-field>
                                    <label>{{ $t("message.passwordUpdate") }}</label>
                                    <div style="display: contents">
                                        <md-input
                                            v-model="user.password"
                                            :name="$t('message.username')"
                                            type="password"
                                            :placeholder="$t('message.passwordUpdate')"
                                            autocomplete="off"
                                        />
                                    </div>
                                </md-field>
                            </b-col>
                            <b-col v-if="!editMode" md="6">
                                <md-field
                                    :class="{
                                        'md-invalid': errors.first($t('message.password')) != null,
                                    }"
                                >
                                    <label>{{ $t("message.password", { num: "6" }) }}</label>
                                    <md-input
                                        :ref="$t('message.password')"
                                        v-model="user.password"
                                        v-validate="editMode ? 'min:6' : 'required|min:6'"
                                        :name="$t('message.password')"
                                        :placeholder="
                                            $t('message.passwordAtLeast', {
                                                num: '6',
                                            })
                                        "
                                        autocomplete="off"
                                        trim
                                        type="password"
                                    ></md-input>
                                    <span class="md-error">{{ errors.first($t("message.password")) }}</span>
                                </md-field>
                            </b-col>
                            <b-col v-if="!editMode" md="6">
                                <md-field
                                    :class="{
                                        'md-invalid': errors.first($t('message.passwordRepeat')) != null,
                                    }"
                                >
                                    <label>{{ $t("message.passwordRepeat") }}</label>
                                    <md-input
                                        v-model="passwordConfirmation"
                                        v-validate="
                                            editMode
                                                ? 'confirmed:' + $t('message.password') + '|min:6'
                                                : 'required|confirmed:' + $t('message.password') + '|min:6'
                                        "
                                        :name="$t('message.passwordRepeat')"
                                        autocomplete="off"
                                        type="password"
                                    ></md-input>
                                    <span class="md-error">{{ errors.first($t("message.passwordRepeat")) }}</span>
                                </md-field>
                            </b-col>
                            <b-col md="6" v-if="checkPermission(PERMISSION_TYPES['role.get'].tr.name)">
                                <b-form-group id="role-input-group" :label="$t('message.role')" label-for="role-input">
                                    <a-select
                                        v-model="user.role"
                                        v-validate="'required'"
                                        :default-value="user.role"
                                        :filter-option="filterOption"
                                        :name="$t('message.role')"
                                        :placeholder="$t('message.selectARole')"
                                        option-filter-prop="children"
                                        show-search
                                        style="width: 100%"
                                    >
                                        <a-select-option :value="undefined">
                                            {{ $t("message.selectARole") }}
                                        </a-select-option>
                                        <a-select-option v-for="r of roles.data" :key="r._id" :value="r._id">
                                            {{ r.name }}
                                        </a-select-option>
                                    </a-select>
                                </b-form-group>
                            </b-col>
                            <b-col md="6">
                                <b-form-group
                                    id="lang-input-group"
                                    :label="$t('message.language')"
                                    label-for="lang-input"
                                >
                                    <a-select
                                        v-model="user.lang"
                                        v-validate="'required'"
                                        :default-value="user.lang"
                                        :filter-option="filterOption"
                                        option-filter-prop="children"
                                        show-search
                                        :name="$t('message.language')"
                                        style="width: 100%"
                                    >
                                        <a-select-option v-for="(r, ii) of languages" :value="r.code" :key="ii">
                                            {{ r.originalName }}
                                        </a-select-option>
                                    </a-select>
                                </b-form-group>
                            </b-col>

                            <b-col md="12">
                                <md-field
                                    :class="{
                                        'md-invalid': errors.first($t('message.autoChatAssignLimit')) != null,
                                    }"
                                >
                                    <label name="auto-assign">{{ $t("message.autoChatAssignLimit") }}</label>
                                    <md-input
                                        id="auto-assign-limit-input"
                                        v-model="user.autoAssignLimit"
                                        :name="$t('message.autoChatAssignLimit')"
                                        autocomplete="off"
                                        class="mt-2"
                                        min="-1"
                                        type="number"
                                    ></md-input>
                                    <span class="md-error">{{ errors.first($t("message.autoChatAssignLimit")) }}</span>
                                </md-field>
                                <small>-{{ $t("message.chatAssignInfo") }}</small>
                            </b-col>
                            <b-col md="12">
                                <div>
                                    <label class="typo__label">{{ $t("message.relatedDepartments") }}</label>
                                    <multiselect
                                        v-model="selectedDepartments"
                                        :multiple="true"
                                        :options="getSubjectOptions()"
                                        :placeholder="$t('message.relatedDepartments')"
                                        label="text"
                                        track-by="value"
                                    ></multiselect>
                                </div>
                            </b-col>
                            <b-col md="12">
                                <div class="d-flex justify-content-start align-items-center">
                                    <label>{{ $t("message.status") }}</label>
                                    <md-switch
                                        v-model="switchButton"
                                        :disabled="loggedUser._id === user._id"
                                        class="md-primary ml-3"
                                    >
                                        <span v-if="user.enable">{{ $t("message.active") }}</span>
                                        <span v-else>{{ $t("message.passive") }}</span>
                                    </md-switch>
                                </div>
                            </b-col>
                            <b-col md="12">
                                <b-button :disabled="loading" type="submit" variant="primary">
                                    <template v-if="!loading">
                                        <span v-if="!editMode">{{ $t("message.save") }}</span>
                                        <span v-else>{{ $t("message.update") }}</span>
                                    </template>
                                    <template v-else>
                                        <font-awesome-icon icon="spinner" spin />
                                    </template>
                                </b-button>
                                <b-button @click="hideModal">
                                    <template> {{ $t("message.cancelText") }}</template>
                                </b-button>
                            </b-col>
                        </b-row>
                    </b-col>
                </b-row>
            </b-form>
        </Modal2>

        <Modal2
            v-if="isTransferModalShow"
            :title="$t('message.updateOwnerUsers')"
            @cancel="hideTransferModal"
            @ok="confirmTransfer"
        >
            <div>
                <p>
                    {{ $t("message.updateOwnerUsersDesc") }}
                </p>
                <CustomPicker
                    v-model="selectedNewOwner"
                    :items="userOptions"
                    idKey="_id"
                    labelKey="name"
                    :title="$t('message.selectUser')"
                    icon="person"
                    :multiple="false"
                />
            </div>
        </Modal2>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { library } from "@fortawesome/fontawesome-svg-core";
import {
    faCheckCircle,
    faClock,
    faGlobeEurope,
    faMinusCircle,
    faPencilAlt,
    faPlusCircle,
    faPowerOff,
    faSpinner,
    faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { log2 } from "../../../../../utils/log2";
import * as _ from "lodash";
import { sendSocketMessage } from "../../../../../utils/socketStream";
import Pagination from "../../../../../components/Pagination.vue";
import ActionEdit from "../../../../../components/svg/ActionEdit.vue";
import ActionDelete from "../../../../../components/svg/ActionDelete.vue";
import Multiselect from "vue-multiselect";
import languages from "../../../../../language/languages.json";
import { getPersistenceData } from "../../../../../utils/persistenceStorage";
import Modal2 from "../../../../../components/common/Modal2.vue";
import { PERMISSION_TYPES } from "../../../../../language/permissionTypes";
import dmIcon from "../../../../../components/svg/DmIcon.vue";
import SYSTEM_PUBSUB from "../../../../../utils/systemPubSub";
import SupsisTable from "../../../../../components/SupsisTable.vue";
import SupsisButton from "../../components/SupsisButton.vue";
import CreateButton from "../../../../../components/CreateButton.vue";
import AgentAvatar from "../../../../../components/common/AgentAvatar.vue";
import CustomPicker from "@/components/pickers/CustomPicker.vue";

library.add(
    faPencilAlt,
    faTimes,
    faPlusCircle,
    faSpinner,
    faCheckCircle,
    faClock,
    faMinusCircle,
    faPowerOff,
    faGlobeEurope
);

const userInitialObject = {
    _id: null,
    username: "",
    email: "",
    password: "",
    role: undefined,
    subjects: [],
    autoAssignLimit: -1,
    enable: true,
    lang: "tr",
};
const log = log2("Users");
export default {
    name: "Users",
    components: {
        CreateButton,
        SupsisButton,
        SupsisTable,
        Pagination,
        ActionEdit,
        ActionDelete,
        Multiselect,
        Modal2,
        dmIcon,
        AgentAvatar,
        CustomPicker,
    },
    data() {
        return {
            languages,
            lastFetchTS: Date.now(),
            isBusy: true,
            getTimerId: null,
            loading: false,
            submitClicked: false,
            user: _.cloneDeep(userInitialObject),
            searchText: this.$route.query.searchText || undefined,
            editMode: false,
            passwordConfirmation: "",
            switchButton: true,
            cols: () => [
                {
                    field: "Avatar",
                    key: "avatar",
                    title: "",
                    width: 50,
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return h(AgentAvatar, {
                            props: {
                                userID: row._id,
                                size: 30,
                                isNamed: false,
                            },
                        });
                    },
                },
                {
                    field: "name",
                    key: "name",
                    title: this.$t("message.username"),
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return row.name.length > 15 ? row.name.slice(0, 11) + "..." : row.name;
                    },
                },
                {
                    field: "email",
                    key: "email",
                    title: this.$t("message.email"),
                },
                {
                    field: "enable",
                    key: "enable",
                    title: this.$t("message.status"),
                    copyDisabled: true,
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return row.enable ? (
                            <div class="material-icons text-success">check</div>
                        ) : (
                            <div class="material-icons text-danger">close</div>
                        );
                    },
                },
                {
                    field: "lastLoginPlatform",
                    key: "lastLoginPlatform",
                    title: "Platform",
                    copyDisabled: true,
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return row.lastLoginPlatform === "mobile" ? (
                            <div class="mr-2 text-primary material-icons-text">
                                <div class="material-icons text-primary">smartphone</div>
                                Mobile
                            </div>
                        ) : (
                            <div class="material-icons-text">
                                <div class="material-icons text-primary">language</div>
                                WEB
                            </div>
                        );
                    },
                },
                {
                    field: "roleSlot",
                    key: "roleSlot",
                    title: this.$t("message.role"),
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return row.role ? row.role.name : "-";
                    },
                },
                {
                    field: "subjects",
                    key: "subjects",
                    title: this.$t("message.departments"),
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        const names = this.getUserDepartmentNames(row);
                        return (
                            <div
                                v-tippy={{
                                    followCursor: true,
                                    placement: "top-right",
                                    theme: this.isDarkMode ? "light-border" : "google",
                                }}
                                content={names}
                            >
                                {names}
                            </div>
                        );
                    },
                },
                {
                    align: "center",
                    field: "actionsSlot",
                    key: "actionsSlot",
                    copyDisabled: true,
                    title: this.$t("message.transactions"),
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        const renderEditButton = () => {
                            return (
                                this.checkPermission(PERMISSION_TYPES["user.update"].tr.name) && (
                                    <a-button
                                        class="actionButton d-flex align-items-center"
                                        on-click={() => this.edit(row._id)}
                                    >
                                         <span class="material-icons" style="color:#1bc5bd;">edit</span>
                                    </a-button>
                                )
                            );
                        };
                        const renderDeleteButton = () => {
                            return (
                                row._id !== this.loggedUser._id &&
                                this.checkPermission(PERMISSION_TYPES["user.delete"].tr.name) && (
                                    <a-button
                                        class="actionButton d-flex align-items-center"
                                        on-click={() => this.remove(row._id)}
                                    >
                                        <ActionDelete />
                                    </a-button>
                                )
                            );
                        };
                        const renderDMButton = () => {
                            return (
                                this.checkPermission(PERMISSION_TYPES["user.delete"].tr.name) && (
                                    <a-button
                                        class="actionButton d-flex align-items-center"
                                        on-click={() => this.startDM(row._id)}
                                    >
                                        <dmIcon />
                                    </a-button>
                                )
                            );
                        };
                        return (
                            <div class="d-flex w-100 justify-content-end">
                                {renderEditButton()}
                                {renderDMButton()}
                                {renderDeleteButton()}
                            </div>
                        );
                    },
                },
            ],
            paginationCursor: {
                total: 0,
                page: 1,
                perPage: !isNaN(getPersistenceData("users-page-size") || "undefined")
                    ? parseInt(getPersistenceData("users-page-size"))
                    : 10,
            },
            tableOptions: {
                id: "users-table",
                scrollWidth: "1000px",
                rowStyleOption: {
                    stripe: true,
                },
                eventCustomOption: {
                    bodyCellEvents: () => {
                        return {
                            dblclick: (event) => {
                                const columnIndex = event.target.cellIndex;
                                const column = this.cols()[columnIndex];
                                if (!column || column.copyDisabled) {
                                    return;
                                }

                                let text = event.toElement.innerText;
                                let textArea = document.createElement("textarea");
                                textArea.value = text;
                                document.body.appendChild(textArea);
                                textArea.select();
                                document.execCommand("copy");
                                document.body.removeChild(textArea);
                                this.$message.success({
                                    content: this.$t("message.copiedClipboard"),
                                    duration: 2,
                                    key: "saved",
                                });
                            },
                        };
                    },
                },
            },
            renderCount: 0,
            currentSort: {
                columnKey: this.$route.query.columnKey || "name",
                order: this.$route.query.order || "ascend",
            },
            users: {
                total: 0,
                count: 0,
                data: [],
            },
            selectedDepartments: [],
            isModalShow: false,
            queryAction: this.$route.query.action,
            showUpgrade: false,
            groupingType: "none",
            isPickerVisible: false,
            openPanels: new Set(),
            groupingOptions: [
                { value: "none", label: this.$t("message.noGrouping") },
                { value: "status", label: this.$t("message.status") },
                { value: "platform", label: this.$t("message.platform") },
                { value: "role", label: this.$t("message.role") },
                { value: "departments", label: this.$t("message.departments") },
                { value: "language", label: this.$t("message.language") },
            ],
            isTransferModalShow: false,
            selectedNewOwner: null,
            userOptions: [],
        };
    },
    computed: {
        PERMISSION_TYPES() {
            return PERMISSION_TYPES;
        },
        ...mapState({
            userPassword: (state) => state.auth.user,
            /*users: state => state.user.users,*/
            roles: (state) => state.role.roles,
            plan: (state) => _.get(state.setting.settings, "plan"),
            settings: (state) => state.setting.settings,
            subjects: (state) => state.subject.subjects.data,
            loggedUser: (state) => state.auth.user || state.auth.adminUser,
            site: (state) => state.site.site,
            isAdmin: (state) => state.auth.adminUser != null,
            currentPlans: (state) => state.site.currentPlans,
            features: (state) => state.site.features,
            plans: (state) => state.site.plans,
            activeUserCount: (state) => state.user.activeUserCount,
        }),
        ...mapGetters({
            isDarkMode: "user/isDarkMode",
            checkPermission: "auth/checkPermission",
        }),
        isPermissionsEmpty() {
            return this.userPassword.role.permissions.length === 0;
        },
        shouldShowContent() {
            return this.isAdmin || this.isPermissionsEmpty;
        },
        getGroupButtonText() {
            switch (this.groupingType) {
                case "status":
                    return this.$t("message.status");
                case "platform":
                    return this.$t("message.platform");
                case "role":
                    return this.$t("message.role");
                case "departments":
                    return this.$t("message.departments");
                case "language":
                    return this.$t("message.language");
                case "none":
                    return this.$t("message.noGrouping");
                default:
                    return this.$t("message.group2");
            }
        },
    },
    methods: {
        ...mapActions({
            getUser: "user/getUser",
            getUsers: "user/getUsers",
            getRoles: "role/getRoles",
            createUser: "user/createUser",
            updateUser: "user/updateUser",
            deleteUser: "user/deleteUser",
            getActiveUserCount: "user/getActiveUserCount",
        }),
        queryCreateCheck() {
            if (this.$route.query.action === "create") {
                this.openCreateModal();
            }
        },
        startDM(id) {
            this.$router.push("/chat?tabIndex=teamChat");
            setTimeout(() => {
                SYSTEM_PUBSUB.publish(SYSTEM_PUBSUB.MESSAGE_TYPES.START_AGENT_CHAT, { userId: id });
            }, 500);
        },
        closeModal() {
            this.isModalShow = false;
        },
        async hideModal() {
            await this.reset();
            await this.closeEditMode();
            this.closeModal();
        },
        showModal() {
            this.isModalShow = true;
        },
        closeEditMode() {
            this.editMode = false;
        },
        getSubjectOptions() {
            return this.subjects.map((p) => {
                const validLangs = Object.keys(p.languageContent);
                const lang = validLangs.includes(this.$i18n.locale) ? this.$i18n.locale : validLangs[0];
                return {
                    text: _.get(p, `languageContent.${lang}.name`),
                    value: p._id,
                };
            });
        },
        getUserDepartmentNames(user) {
            return (user.subjects || []).length
                ? this.subjects
                      .filter((x) => (user.subjects || []).includes(x._id))
                      .map(
                          (x) =>
                              _.get(x, `languageContent.${this.$i18n.locale}.name`) ||
                              _.get(x, `languageContent.${Object.keys(x.languageContent)[0]}.name`)
                      )
                      .join(",")
                      .toLowerCase()
                : this.$t("message.allDepartments");
        },
        tableChange(pagination, filters, sorter) {
            log("tableChange", { pagination, filters, sorter });
            this.paginationCursor.perPage = pagination.pageSize;
            log("tableChange", { pagination, filters, sorter });
            const newSort = {
                columnKey: sorter.columnKey,
                order: sorter.order,
            };
            const oldSort = this.currentSort;
            log("show me current Sort", {
                currentSort: this.currentSort,
                newSort,
            });
            if (!_.isEqual(newSort, oldSort)) {
                log("sorting changed rendering", { newSort, oldSort });
                this.currentSort = newSort;
                this.renderCount += 1;
                this.$forceUpdate();
            }
        },
        filterOption(input, option) {
            return option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
        },
        getFilteredRows() {
            // State'ten tüm kullanıcıları al
            const allUsers = this.users.data;
            
            // Toplam sayıyı güncelle
            this.paginationCursor.total = allUsers.length;
            
            // Sayfalama uygula
            const start = (this.paginationCursor.page - 1) * this.paginationCursor.perPage;
            const end = start + this.paginationCursor.perPage;
            
            return allUsers.slice(start, end);
        },
        pageChange(page, pageSize) {
            log("pageChange", { page, pageSize });
            this.paginationCursor.page = page;
            this.paginationCursor.perPage = pageSize;
        },
        onShowSizeChange(current, pageSize) {
            log("onShowSizeChange", current, pageSize);
            this.paginationCursor.page = current;
            this.paginationCursor.perPage = pageSize;
        },
        updateActiveAgentCount() {
            this.getActiveUserCount();
        },
        debouncedGet(getRoles = false, showLoading = true, interval = 2000) {
            if (!!this.getTimerId) clearTimeout(this.getTimerId);
            this.getTimerId = setTimeout(() => {
                this.get(getRoles, showLoading);
            }, interval);
        },
        showSupsisPlans() {
            this.$router.push("/billing/plans/supsis");
        },
        get(getRoles = false, showLoading = true) {
            if (showLoading) this.isBusy = true;
            const query = this.searchText === "" && this.searchText == null ? undefined : this.searchText;
            const pagination = { perPage: this.paginationCursor.perPage, page: this.paginationCursor.page };
            const sorting = {
                sortBy: this.currentSort.columnKey,
                sortOrder: this.currentSort.order,
            };
            this.updateQueryParams("columnKey", sorting.sortBy);
            this.updateQueryParams("order", sorting.sortOrder);
            this.updateQueryParams("page", pagination.page);
            this.updateQueryParams("perPage", pagination.perPage);
            this.updateQueryParams("searchText", query);
            this.getUsers({
                perPage: pagination.perPage,
                page: pagination.page,
                query,
                sortBy: sorting.sortBy,
                sortOrder: sorting.sortOrder,
                updateStore: false,
            })
                .then((x) => {
                    log.debug("users fetched", x);
                    if (!_.isEqual(this.users, x.data)) {
                        this.users = x.data;
                        this.paginationCursor.total = x.data.total;
                    }

                    if (getRoles) {
                        this.getRoles({ perPage: 100, page: 1 });
                    }
                })
                .finally(() => {
                    if (showLoading) this.isBusy = false;
                    this.lastFetchTS = Date.now();
                    if (this.queryAction == "create") this.openCreateModal();
                });
            this.updateActiveAgentCount();
        },
        updateQueryParams(key, value) {
            if (value === "" || value == null) {
                let query = Object.assign({}, this.$route.query);
                delete query[key];
                this.$router.replace({ query });
            } else {
                let { query } = this.$route;
                this.$router.replace({
                    query: { ...query, [key]: encodeURI(value) },
                });
            }
        },
        getCurrentPlanName() {
            const planAction = this.currentPlans.find((cp) => cp.planType === "supsis");
            const plan = this.plans.find((plan) => plan._id === planAction?.planId);
            return plan ? `${plan.title} ${plan.altTitle}` : "Supsis Free";
        },

        openCreateModal() {
            this.user.name = "Supsis Ai";
            this.user.email = "supsis@supsis.ai";
            this.selectedDepartments = [];
            this.showModal();
            this.editMode = false;
            //this.$bvModal.show("user-form");
        },
        edit(id) {
            log("edit", id);
            const maybeUser = this.users.data.find((x) => x._id === id);
            if (maybeUser) {
                this.editMode = true;
                this.user = _.cloneDeep(maybeUser);
                if (typeof this.user.role === "object") this.user.role = this.user.role._id;
                this.user.password = "";
                this.switchButton = this.user.enable;
                log("updating user", this.user);

                this.selectedDepartments = this.subjects
                    .filter((x) => this.user.subjects.includes(x._id))
                    .map((p) => ({
                        text:
                            _.get(p, `languageContent.${this.$i18n.locale}.name`) ||
                            _.get(p, `languageContent.${Object.keys(p.languageContent)[0]}.name`),
                        value: p._id,
                    }));
                this.showModal();
                //this.$bvModal.show("user-form");
            }
        },
        remove(id) {
            this.$dialog
                .confirm("Bu kullanıcıyı silmek istediğinizden emin misiniz?", {
                    okText: "Devam Et",
                    cancelText: "İptal",
                })
                .then(() => {
                    this.showTransferModal(id);
                });
        },
        async showTransferModal(userId) {
            this.selectedUserId = userId;

            const allUsers = await this.getAllUsers();
            this.userOptions = allUsers.filter(user => user._id !== userId);

            this.isTransferModalShow = true;
        },
        hideTransferModal() {
            this.isTransferModalShow = false;
            this.selectedNewOwner = null;
        },
        confirmTransfer() {
            const transferOwnerShipToUserId = this.selectedNewOwner ? this.selectedNewOwner : null;
            this.deleteUser({ id: this.selectedUserId, transferOwnerShipToUserId })
                .then(() => {
                    this.get(true);
                    this.$toasted.show("Kullanıcı silindi.", {
                        duration: 3000,
                        position: "bottom-center",
                    });
                })
                .catch((err) => {
                    console.error(err);
                    this.$toasted.show("Kullanıcı silme hatası.", {
                        duration: 3000,
                        position: "bottom-center",
                    });
                })
                .finally(() => {
                    this.hideTransferModal();
                });
        },
        reset() {
            log("reset");
            this.user = _.cloneDeep(userInitialObject);
            this.submitClicked = null;
            this.passwordConfirmation = "";
        },
        handleSave() {
            log("handleSave");
            this.submitClicked = true;

            this.user.enable = this.switchButton;
            return this.$validator.validateAll().then((response) => {
                if (response) {
                    if (!this.user.role) {
                        this.user.role = this.loggedUser.role._id;
                    }
                    this.loading = true;
                    const result = this.user._id
                        ? this.updateUser({ user: this.user, siteId: this.site._id })
                        : this.createUser(this.user);
                    const operation = this.user._id ? "update" : "create";

                    result
                        .then(() => {
                            log("operation completed", { operation });
                            this.$toasted.show(this.$t("message.userSaved"), {
                                duration: 3000,
                                position: "bottom-center",
                            });

                            if (operation === "update") {
                                const socketMessageId = this.isAdmin ? "admin user-updated" : "user user-updated";
                                sendSocketMessage(socketMessageId, {
                                    userId: this.user._id,
                                    siteId: this.site._id,
                                    isUserScopesChanged: true,
                                });
                            }
                            this.reset();
                            this.get();
                            sendSocketMessage("team refresh-chats", {
                                siteId: this.site._id,
                            });
                        })
                        .catch((err) => {
                            log.error(err.response, err.message);
                            const errMsg =
                                err.response && err.response.data && err.response.data.localizedMessages
                                    ? err.response.data.localizedMessages[this.$i18n.locale]
                                    : err.message;
                            this.$toasted.error(this.$t("message.emailAddressYouProvide"), {
                                duration: 4000,
                                position: "bottom-center",
                            });
                        })
                        .finally(() => {
                            this.loading = false;
                            this.submitClicked = false;
                            this.hideModal();
                        });
                } else {
                    this.loading = false;
                }
            });
        },

        searchTextHandle(e) {
            this.searchText = e.target.value;
        },
        onSearch() {
            this.debouncedGet(false, true, 0);
        },
        togglePicker() {
            this.isPickerVisible = !this.isPickerVisible;
        },
        onGroupingChange(value) {
            this.groupingType = value;
            this.$nextTick(() => {
                const groups = Object.keys(this.getGroupedUsers());
                this.openPanels = new Set(groups);
            });
            setTimeout(() => {
                this.isPickerVisible = false;
            }, 100);
        },
        getGroupedUsers() {
            switch (this.groupingType) {
                case "status":
                    return this.groupByStatus();
                case "platform":
                    return this.groupByPlatform();
                case "role":
                    return this.groupByRole();
                case "departments":
                    return this.groupByDepartments();
                case "language":
                    return this.groupByLanguage();
                default:
                    return {};
            }
        },
        groupByStatus() {
            const active = this.getFilteredRows().filter((user) => user.enable);
            const passive = this.getFilteredRows().filter((user) => !user.enable);
            return {
                active,
                passive,
            };
        },
        groupByPlatform() {
            return _.groupBy(this.getFilteredRows(), (user) => {
                return user.platform || "Web";
            });
        },
        groupByRole() {
            return _.groupBy(this.getFilteredRows(), (user) => user.role?.name);
        },
        groupByDepartments() {
            const groupedUsers = {};

            this.getFilteredRows().forEach((user) => {
                if (!user.subjects || user.subjects.length === 0) {
                    // Departmanı olmayan kullanıcılar
                    const noDeptKey = this.$t("message.noDepartment");
                    if (!groupedUsers[noDeptKey]) {
                        groupedUsers[noDeptKey] = [];
                    }
                    groupedUsers[noDeptKey].push(user);
                } else {
                    // Her bir departman için kullanıcıyı ekle
                    user.subjects.forEach((subjectId) => {
                        const subject = this.subjects.find((s) => s._id === subjectId);
                        if (subject) {
                            const deptName = subject.languageContent[this.$i18n.locale].name;
                            if (!groupedUsers[deptName]) {
                                groupedUsers[deptName] = [];
                            }
                            groupedUsers[deptName].push(user);
                        }
                    });
                }
            });

            return groupedUsers;
        },
        groupByLanguage() {
            return _.groupBy(this.getFilteredRows(), "lang");
        },
        togglePanel(groupName) {
            if (this.openPanels.has(groupName)) {
                this.openPanels.delete(groupName);
            } else {
                this.openPanels.add(groupName);
            }
            // Force re-render
            this.$forceUpdate();
        },
        isPanelOpen(groupName) {
            return this.openPanels.has(groupName);
        },
        initializePanels() {
            const groups = Object.keys(this.getGroupedUsers());
            this.openPanels = new Set(groups);
            // Force re-render
            this.$forceUpdate();
        },
        getGroupDisplayName(groupName) {
            if (this.groupingType === "status") {
                return groupName === "active" ? this.$t("message.activeUsers") : this.$t("message.passiveUsers");
            }
            if (this.groupingType === "platform") {
                switch (groupName) {
                    case "Web":
                        return this.$t("message.web");
                    case "Mobile":
                        return this.$t("message.mobile");
                    default:
                        return groupName;
                }
            }
            return groupName;
        },
        getGroupColor(groupName) {
            const hashCode = (str) => {
                let hash = 0;
                for (let i = 0; i < str.length; i++) {
                    hash = str.charCodeAt(i) + ((hash << 5) - hash);
                }
                return hash;
            };

            const hash = hashCode(groupName);
            const hue = Math.abs(hash % 360); // Hue value between 0 and 360
            const saturation = 40; // Moderate saturation for pastel colors
            const lightness = 85; // Higher lightness for a lighter look

            // Adjust hue to favor blue, yellow, and green tones
            if (hue < 60) {
                return `hsl(${hue}, ${saturation}%, ${lightness}%)`; // Yellow tones
            } else if (hue < 180) {
                return `hsl(${hue}, ${saturation}%, ${lightness}%)`; // Green tones
            } else {
                return `hsl(${hue - 60}, ${saturation}%, ${lightness}%)`; // Blue tones
            }
        },
        getTextColor(groupName) {
            const hashCode = (str) => {
                let hash = 0;
                for (let i = 0; i < str.length; i++) {
                    hash = str.charCodeAt(i) + ((hash << 5) - hash);
                }
                return hash;
            };

            const hash = hashCode(groupName);
            const hue = Math.abs(hash % 360); // Get the hue from the group name

            // Define text color based on a more vibrant color palette
            if (hue < 60) {
                return "text-warning"; // Yellow text
            } else if (hue < 180) {
                return "text-success"; // Green text
            } else {
                return "text-info"; // Blue text
            }
        },
        getAlertColor(groupName) {
            const hashCode = (str) => {
                let hash = 0;
                for (let i = 0; i < str.length; i++) {
                    hash = str.charCodeAt(i) + ((hash << 5) - hash);
                }
                return hash;
            };

            const hash = hashCode(groupName);
            const hue = Math.abs(hash % 360); // Hue value between 0 and 360

            // Define alert color based on a more vibrant color palette
            if (hue < 60) {
                return "alert-warning"; // Yellow alert color
            } else if (hue < 180) {
                return "alert-success"; // Green alert color
            } else {
                return "alert-info"; // Blue alert color
            }
        },
        async getAllUsers() {
            const response = await this.getUsers({
                perPage: 0,
                page: 1,
                updateStore: false,
            });
            return response.data.data;
        },
    },

    mounted() {
        log("mounted");
        this.get(true);
        this.timerId = setInterval(() => {
            const tookTime = Date.now() - this.lastFetchTS;
            if (tookTime > 5000) {
                this.debouncedGet(false, false, 0);
            }
        }, 5000);

        this.queryCreateCheck();
        document.addEventListener("click", (e) => {
            if (this.$refs.groupContainer && !this.$refs.groupContainer.contains(e.target)) {
                this.isPickerVisible = false;
            }
        });
    },

    beforeDestroy() {
        if (this.timerId) clearInterval(this.timerId);
    },

    updated() {
        if (this.$validator.locale !== this.$i18n.locale) this.$validator.localize(this.$i18n.locale);
    },
    watch: {
        paginationCursor: {
            deep: true,
            handler(newVal, oldVal) {
                this.debouncedGet(false, true, 1);
            },
        },
        $route() {
            this.queryCreateCheck();
        },
        searchText(newValue, oldValue) {
            this.debouncedGet();
        },
        currentSort(newValue, oldValue) {
            if (newValue) {
                this.debouncedGet(false, true, 0);
            }
        },
        selectedDepartments(newValue) {
            this.user.subjects = newValue.map((x) => x.value);
        },
        groupingType: {
            immediate: true,
            handler(newValue) {
                if (newValue !== "none") {
                    this.$nextTick(() => {
                        this.initializePanels();
                    });
                }
            },
        },
    },
};
</script>

<style src="../../../../../../node_modules/vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="scss">
.users-supsis-table {
    #avatar-wrapper {
        .offlineBorder {
            border: 2px solid #aeaeae;
        }
        .onlineBorder {
            border: 2px solid transparent;
        }
        #online-dot {
            bottom: 0 !important;
            right: 0 !important;
            height: 25% !important;
            width: 25% !important;
        }
        #offline-dot {
            bottom: 0 !important;
            right: 0 !important;
            height: 25% !important;
            width: 25% !important;
        }
    }
}
#users {
    position: relative;
    height: 100%;

    .material-icons-text {
        display: flex;
        align-items: center;
        gap: 3px;

        .material-icons {
            font-size: 20px;
        }
    }

    .actionButton {
        background: none;
    }

    .fab {
        width: 50px;
        height: 50px;
        border-radius: 25px;
        background-color: white;
        position: absolute;
        bottom: 20px;
        z-index: 9999;
        transition: all 0.3s;
        box-shadow: 0 0 0 0 rgba(100, 100, 100, 1);
        transform: scale(1);
        animation: pulse 3s infinite;
        cursor: pointer;
    }

    #btn-change-plan > a > b:hover {
        color: whitesmoke;
    }

    @keyframes pulse {
        0% {
            transform: scale(0.95);
            box-shadow: 0 0 0 0 rgba(100, 100, 100, 0.5);
        }

        70% {
            transform: scale(1);
            box-shadow: 0 0 0 10px #00000000;
        }

        100% {
            transform: scale(0.95);
            box-shadow: 0 0 0 0 #00000000;
        }
    }
}

.group-section {
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 4px;
    margin-bottom: 16px;

    .group-header {
        padding: 12px 16px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        cursor: pointer;
        user-select: none;
        background: #fafafa;
        border-bottom: 1px solid #e8e8e8;

        &:hover {
            background: #f5f5f5;
        }

        h3 {
            margin: 0;
            font-size: 14px;
            color: #333;
            font-weight: 500;
        }

        .toggle-icon {
            font-size: 20px;
            color: #666;
        }

        .count {
            background: #eee;
            padding: 2px 8px;
            border-radius: 12px;
            font-size: 12px;
            color: #666;
        }
    }
}

#header-content {
    position: relative;
    padding: 10px;
    background-color: white;
    z-index: 101;
}
</style>
